import { initProductTour, productTour } from './initProductTour';
import type { IProductTourService } from './initProductTour';
import { App, InjectionKey, inject } from 'vue';

/**
 * Injection key for providing and injecting the product tour instance in Vue components.
 * Use with app.provide and inject to access product tour throughout the app.
 */
const ProductTourKey: InjectionKey<IProductTourService> = Symbol('ProductTour');

/**
 * Vue plugin for initializing and providing the product tour instance.
 *
 * Usage:
 *   app.use(ProductTourVuePlugin, { token: 'your-token', debug: true })
 *
 * This will initialize product tour and make it available via inject/useProductTour in all components.
 */
export const ProductTourVuePlugin = {
  install(app: App, options: Parameters<typeof initProductTour>[0]) {
    initProductTour(options);
    app.provide(ProductTourKey, productTour);
  },
};

/**
 * Composable to access the product tour instance in Vue components.
 *
 * Throws an error if the plugin was not installed.
 *
 * Usage:
 *   const productTour = useProductTour();
 */
export function useProductTour(): IProductTourService {
  const instance = inject<IProductTourService>(ProductTourKey);
  if (!instance)
    throw new Error(
      'ProductTour instance not provided. Did you forget to install ProductTourVuePlugin?'
    );
  return instance;
}
